import { createApi } from '@reduxjs/toolkit/query/react';
import { baseEditorQuery } from './config';
import {
  CreateEditorThreadRequest,
  CreateThreadResponse,
  SearchThreadsResponse,
  Thread,
  UpdateEditorThreadRequest,
} from 'src/types';

type CommonEditorArgs = { user_id: string; thread_id: string };

export enum EditorTags {
  Threads = 'Threads',
}

export const editorApi = createApi({
  reducerPath: 'editorApi',
  tagTypes: [EditorTags.Threads],
  baseQuery: baseEditorQuery,
  endpoints: (builder) => ({
    getEditorThreads: builder.query<SearchThreadsResponse, { user_id: string }>(
      {
        query: ({ user_id }) => ({
          url: `users/${user_id}/threads`,
        }),
        providesTags: [EditorTags.Threads],
      },
    ),

    getEditorThreadById: builder.query<Thread, CommonEditorArgs>({
      query: ({ user_id, thread_id }) => ({
        url: `users/${user_id}/threads/${thread_id}`,
      }),
      providesTags: (_, __, { thread_id }) => {
        return [{ type: EditorTags.Threads, id: thread_id }];
      },
    }),

    createEditorThread: builder.mutation<
      CreateThreadResponse,
      CreateEditorThreadRequest
    >({
      query: ({ user_id, ...body }) => ({
        url: `users/${user_id}/threads`,
        method: 'POST',
        body,
      }),
      async onQueryStarted({ user_id, ...body }, { dispatch, queryFulfilled }) {
        try {
          const {
            data: { thread },
          } = await queryFulfilled;

          const {
            thread_id,
            thread_title,
            thread_type,
            created_at,
            updated_at,
          } = thread;
          const newSimpleThread = {
            user_id,
            thread_id,
            thread_title,
            thread_type,
            created_at,
            updated_at,
          };

          if (!thread_id) {
            return;
          }

          dispatch(
            editorApi.util.updateQueryData(
              'getEditorThreads',
              { user_id },
              (draft) => {
                draft.threads.unshift(newSimpleThread);
              },
            ),
          );

          dispatch(
            editorApi.util.upsertQueryData(
              'getEditorThreadById',
              { user_id, thread_id },
              {
                ...thread,
                // TODO(olha): WIP Editor remove hen BE fix POST response
                artifact: body.thread.artifact,
              },
            ),
          );
        } catch {}
      },
    }),

    updateEditorThread: builder.mutation<
      CreateThreadResponse,
      UpdateEditorThreadRequest
    >({
      query: ({ user_id, thread_id, ...body }) => ({
        url: `users/${user_id}/threads/${thread_id}`,
        method: 'PATCH',
        body,
      }),
      async onQueryStarted({ user_id, ...body }, { dispatch, queryFulfilled }) {
        try {
          const {
            data: { thread },
          } = await queryFulfilled;

          dispatch(
            editorApi.util.updateQueryData(
              'getEditorThreadById',
              { user_id, thread_id: body.thread_id },
              (draft) => {
                if (draft) {
                  Object.assign(draft, thread);
                }
              },
            ),
          );

          dispatch(
            editorApi.util.updateQueryData(
              'getEditorThreads',
              { user_id },
              (draft) => {
                const threadIndex = draft.threads.findIndex(
                  (thread) => thread.thread_id === body.thread_id,
                );

                if (threadIndex !== -1) {
                  draft.threads[threadIndex] = {
                    ...draft.threads[threadIndex],
                    ...thread,
                  };
                }
              },
            ),
          );
        } catch {}
      },
    }),

    deleteEditorThread: builder.mutation<void, CommonEditorArgs>({
      query: ({ user_id, thread_id }) => ({
        url: `users/${user_id}/threads/${thread_id}`,
        method: 'DELETE',
      }),
      async onQueryStarted(
        { user_id, thread_id },
        { dispatch, queryFulfilled },
      ) {
        const patchThreads = dispatch(
          editorApi.util.updateQueryData(
            'getEditorThreads',
            { user_id },
            (draft) => {
              draft.threads = draft.threads.filter(
                (thread) => thread.thread_id !== thread_id,
              );
            },
          ),
        );
        const patchDetail = dispatch(
          editorApi.util.updateQueryData(
            'getEditorThreadById',
            { user_id, thread_id },
            () => {
              return {} as unknown as Thread;
            },
          ),
        );
        try {
          await queryFulfilled;
        } catch {
          patchThreads.undo();
          patchDetail.undo();
        }
      },
    }),
  }),
});

export const {
  useUpdateEditorThreadMutation,
  useGetEditorThreadsQuery,
  useGetEditorThreadByIdQuery,
  useLazyGetEditorThreadByIdQuery,
  useCreateEditorThreadMutation,
  useDeleteEditorThreadMutation,
} = editorApi;
